import type { OverviewChartRow, KpiCatalogEntry } from "@nexus/contracts";
import {
  compileKpiFormula,
  evalKpi,
  emptyColumnTotals,
  type CompiledKpiFormula,
} from "@nexus/kpi-formula";
import { resolveCatalogColorVar } from "@/features/kpi-settings/lib/kpi-color";

/**
 * Core-Mode (My Brand) OverviewChart metric catalog — built from the tenant KPI
 * API (`Kpi.name` is the stable id). Colors resolve from `colorToken` (chart-1…24).
 */

export type MetricFormat = "currency" | "percent" | "ratio" | "number";

/** Maps a catalog format to the `formatMetricValue` union (`number` → `count`). */
export const FORMAT_TO_DISPLAY: Record<MetricFormat, "currency" | "count" | "percent" | "ratio"> = {
  currency: "currency",
  percent: "percent",
  ratio: "ratio",
  number: "count",
};

/** Cross-Scenario column totals — needed for share-of-total metrics (Spend %, Creatives %). */
export type OverviewTotals = Pick<OverviewChartRow, "spend" | "creatives">;

export interface MetricCatalogEntry {
  /** Stable id — persisted in the store; canonical `Kpi.name`. */
  id: string;
  label: string;
  format: MetricFormat;
  decimals: number;
  /** Canonical chart color (CSS var from design-system.css). */
  colorVar: string;
  /** Source expression, or `null` for a raw metric whose name is its own token.
   *  Kept so callers can inspect which columns it reads — see `isChartableOnOverview`. */
  formula: string | null;
  /** `null` ⇒ divide-by-zero / no data ⇒ no bar rendered (distinct from 0). */
  compute: (row: OverviewChartRow, totals: OverviewTotals) => number | null;
}

function catalogFormat(entry: KpiCatalogEntry): MetricFormat {
  if (entry.name === "ROAS") return "ratio";
  if (entry.format === "CURRENCY") return "currency";
  if (entry.format === "PERCENT") return "percent";
  return "number";
}

function compileCompute(entry: KpiCatalogEntry): MetricCatalogEntry["compute"] {
  const expr = entry.formula ?? entry.name;
  let ast: CompiledKpiFormula | null = null;
  try {
    ast = compileKpiFormula(expr);
  } catch {
    return () => null;
  }
  const compiled = ast;
  const isPercent = entry.format === "PERCENT";
  return (row, totals) => {
    const totalsRow = { ...emptyColumnTotals(), ...totals };
    const value = evalKpi(compiled, row, totalsRow);
    if (value === null) return null;
    return isPercent ? value * 100 : value;
  };
}

export function buildMetricCatalog(rows: KpiCatalogEntry[]): MetricCatalogEntry[] {
  const visible = rows.filter((row) => row.visible);
  return visible.map((row) => ({
    id: row.name,
    label: row.displayName,
    format: catalogFormat(row),
    decimals: row.decimals,
    colorVar: resolveCatalogColorVar(row.name, row.index, row.colorToken),
    formula: row.formula,
    compute: compileCompute(row),
  }));
}

export function catalogById(catalog: MetricCatalogEntry[]): Record<string, MetricCatalogEntry> {
  return Object.fromEntries(catalog.map((e) => [e.id, e]));
}

/** Cross-Scenario totals for share-of-total metrics. */
export function sumOverviewTotals(rows: OverviewChartRow[]): OverviewTotals {
  return rows.reduce<OverviewTotals>(
    (acc, r) => ({ spend: acc.spend + r.spend, creatives: acc.creatives + r.creatives }),
    { spend: 0, creatives: 0 },
  );
}

export function computeMetric(
  catalog: MetricCatalogEntry[],
  id: string,
  row: OverviewChartRow,
  totals: OverviewTotals,
): number | null {
  return catalog.find((e) => e.id === id)?.compute(row, totals) ?? null;
}

export function defaultAxisForMetric(
  existingFormats: MetricFormat[],
  entry: MetricCatalogEntry,
): "left" | "right" {
  if (existingFormats.length === 0) return "left";
  return entry.format === "currency" || entry.format === "ratio" ? "left" : "right";
}

/** @deprecated Use `buildMetricCatalog` from the API catalog. Empty until the query resolves. */
export const METRIC_CATALOG: MetricCatalogEntry[] = [];
export const CATALOG_BY_ID: Record<string, MetricCatalogEntry> = {};
